/**
 * Offer dismissal — hide a proactive offer for 7 days, per offer id.
 *
 * When an operator dismisses one of Argo's "can help" offers, we don't want
 * it nagging back the next render. This persists a per-offer-id "hidden
 * until" timestamp to localStorage (same pattern as autonomy.ts /
 * ticketAutopilot.ts flags — per-operator, not shared data) so the dismissal
 * survives reloads. Expired entries self-heal: once the 7 days pass the offer
 * is eligible again, and stale entries are pruned on write.
 *
 * PURE-adjacent: `filterDismissed` takes a clock so tests are deterministic;
 * only `dismissOffer`/`isDismissed` touch localStorage + the current clock.
 */

import type { Offer } from './offers';

const STORAGE_KEY = 'argo-os:offer-dismissals';
const DAY_MS = 86_400_000;
export const DISMISS_DAYS = 7;

/** Map of offer id → epoch ms the dismissal expires at. */
type DismissalState = Record<string, number>;

function read(): DismissalState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    if (parsed && typeof parsed === 'object') return parsed as DismissalState;
    return {};
  } catch {
    return {};
  }
}

function write(state: DismissalState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/** Drop entries whose hide-until has already passed. */
function prune(state: DismissalState, now: number): DismissalState {
  const out: DismissalState = {};
  for (const [id, until] of Object.entries(state)) {
    if (typeof until === 'number' && until > now) out[id] = until;
  }
  return out;
}

/** Hide an offer id for `DISMISS_DAYS` days from `now`. */
export function dismissOffer(id: string, now: number = Date.now()): void {
  const state = prune(read(), now);
  state[id] = now + DISMISS_DAYS * DAY_MS;
  write(state);
  notify();
}

/** True when this offer id is currently within its 7-day hide window. */
export function isDismissed(id: string, now: number = Date.now()): boolean {
  const until = read()[id];
  return typeof until === 'number' && until > now;
}

/** Drop any offers currently dismissed. Deterministic via injected `now`. */
export function filterDismissed(
  offers: Offer[],
  state: DismissalState,
  now: number,
): Offer[] {
  return offers.filter((o) => {
    const until = state[o.id];
    return !(typeof until === 'number' && until > now);
  });
}

/** Read the current (pruned) dismissal map — for filterDismissed callers. */
export function readDismissals(now: number = Date.now()): DismissalState {
  return prune(read(), now);
}

// --- Listener pattern so the dock re-renders on dismiss ---------------------

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeOfferDismissals(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notify(): void {
  for (const fn of listeners) fn();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (ev) => {
    if (ev.key === STORAGE_KEY) notify();
  });
}
